import { useParams, Link } from 'react-router-dom';
import { seoArticles } from '@/mocks/seo-articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useEffect, useMemo } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const jsonLd = useMemo(() => {
    if (!article) return '';
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Organization', name: 'GTBuy Team' },
      publisher: { '@type': 'Organization', name: 'GTBuy Team' },
      datePublished: '2026-07-01',
      dateModified: '2026-07-01',
      mainEntityOfPage: { '@type': 'WebPage', '@id': `https://gtbuy.team/article/${article.slug}` },
      image: article.image,
    });
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-file-search-line w-8 h-8 flex items-center justify-center text-accent-600"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
              Article Not Found
            </h1>
            <p className="text-sm text-foreground-600 mb-6">
              The article you are looking for does not exist or may have been moved.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
      <Navbar />
      <main className="pt-20 pb-16">
        {/* Article Hero */}
        <section className="w-full px-4 md:px-6 mb-10">
          <div className="max-w-4xl mx-auto">
            <div className="w-full aspect-[21/9] rounded-lg overflow-hidden bg-background-100 mb-6">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-400">Articles</span>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-3">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {article.excerpt}
            </p>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <article className="space-y-10">
              {article.sections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    {section.heading}
                  </h2>
                  <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-3">
                    {section.content.split('\n').filter(Boolean).map((paragraph, pIdx) => (
                      <p key={pIdx}>{paragraph.trim()}</p>
                    ))}
                  </div>
                </div>
              ))}
            </article>

            {/* FAQ Section */}
            {article.faqs.length > 0 && (
              <section className="mt-12 pt-10 border-t border-background-200/70">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-white rounded-lg border border-background-200/70 p-5">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <div className="mt-12 text-center py-8 bg-white rounded-lg border border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                Ready to Start Saving?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Use the GTBuy spreadsheet to compare shopping agents and find the best deals on Kaspreadsheet.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-primary-500 text-background-50 dark:text-foreground-950 px-8 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit Kaspreadsheet
                </a>
                <Link
                  to="/"
                  className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}